// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
// @todo: Migrate to 'Switch' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import * as React from 'react';

import DOC_URLS from '../../util/doc_urls';
import {LabeledOptionBlock as LabeledOption} from '../elements/LabeledOptionBlock';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {runsLinePlotConfigDefaults} from '../PanelRunsLinePlot/defaults';
import {DEFAULT_LINE_PLOT_SETTINGS} from '../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {MultiRunAggOptions, SingleRunAggOptions} from './AggOptions';
import {AggRange} from './AggRange';
import {AggregationToggle} from './AggregationToggle';
import {AggType} from './AggType';
import {PlotType} from './PlotType';
import {type BarChartConfig, type PanelGroupingOptionsProps} from './types';

export const PanelGroupingOptions = (props: PanelGroupingOptionsProps) => {
  const {
    absoluteMaxGroupRuns,
    config,
    defaultMaxGroupRuns,
    disabled,
    isGrouped,
    totalRuns,
    updateConfig,
    usingExpressions,
  } = props;
  const {isSingleRun} = useSingleRunModeContext();
  const showAggregationOptions =
    (props.type === 'bars' &&
      (config as BarChartConfig).plotStyle !== 'boxplot' &&
      (config as BarChartConfig).plotStyle !== 'violin') ||
    props.type === 'lines';
  const disableAggregationOptions = props.type === 'lines' && usingExpressions;

  return (
    <Tailwind>
      {isSingleRun ? (
        <SingleRunAggOptions {...props} />
      ) : (
        <MultiRunAggOptions {...props} />
      )}
      {(config.aggregateMetrics === true ||
        ((props.type === 'bars' || props.type === 'lines') &&
          config.metrics != null &&
          config.metrics.length > 1)) && (
        <LabeledOption
          label="Metrics"
          helpText="Aggregate over all metrics"
          option={
            <Switch.Root
              size="medium"
              disabled={disabled}
              checked={config.aggregateMetrics}
              name="aggregate"
              onCheckedChange={isChecked =>
                updateConfig({aggregateMetrics: isChecked})
              }>
              <Switch.Thumb
                size="medium"
                checked={config.aggregateMetrics ?? false}
              />
            </Switch.Root>
          }
        />
      )}
      {isGrouped && (
        <>
          <div className="group-options">
            <div className="group-option">
              {usingExpressions && (
                <div className="hint-text">
                  <p>
                    Grouping functions are disabled while using expressions. In
                    order to reenable, remove all expressions.
                  </p>
                </div>
              )}
              {props.type === 'bars' && (
                <PlotType
                  config={config as BarChartConfig}
                  updateConfig={updateConfig}
                  usingExpressions={usingExpressions}
                />
              )}
              {showAggregationOptions && (
                <AggType
                  config={config}
                  updateConfig={updateConfig}
                  options={{
                    allowMedian: true,
                    defaultGroupAgg: DEFAULT_LINE_PLOT_SETTINGS['groupAgg'],
                    disabled: disableAggregationOptions,
                    includeSum: props.type === 'scalar',
                  }}
                />
              )}

              {showAggregationOptions && (
                <AggRange
                  config={config}
                  disabled={disableAggregationOptions}
                  updateConfig={updateConfig}
                  // scalar plot doesn't include samples
                  includeSamples={props.type !== 'scalar'}
                />
              )}
            </div>
          </div>
        </>
      )}
      {isGrouped && totalRuns >= defaultMaxGroupRuns && (
        <LabeledOption
          label="Sampled runs"
          helpText="Number of runs to sample from for grouping (large numbers may impact performance)."
          docUrl={DOC_URLS.compareMetrics + '#grouping'}
          option={
            <ModifiedDropdown
              lazyLoad
              className="no-wrap-options"
              value={config.groupRunsLimit || defaultMaxGroupRuns}
              style={{minWidth: 60}}
              selection
              options={[
                defaultMaxGroupRuns,
                // limit the absolute maximum number of runs to group so we don't hit performance issues
                Math.min(totalRuns, absoluteMaxGroupRuns),
              ]
                .sort()
                .map(n => ({
                  value: n,
                  text: `${n}`,
                }))}
              onChange={(_, {value}) => {
                if (typeof value === 'number') {
                  updateConfig({
                    groupRunsLimit: value,
                  });
                }
              }}
            />
          }
        />
      )}
      {isGrouped && props.type === 'lines' && (
        <AggregationToggle
          // @ts-ignore this will work even if it doesn't exist, and TS can't infer the narrowing here properly
          checked={config.windowing ?? runsLinePlotConfigDefaults.windowing} // this defaults to true
          disabled={false}
          updateConfig={updateConfig}
        />
      )}
    </Tailwind>
  );
};
